import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Skeleton, Alert as MuiAlert, Button, Typography } from '@mui/material';
import { UpdateDisabled } from '@mui/icons-material';
import { io } from 'socket.io-client';
import api from '../../../services/api';
import UrgencyStrip from './UrgencyStrip';
import ActionQueue from './ActionQueue';
import SummaryStatsBar from './SummaryStatsBar';
import ActivityStream from './ActivityStream';
import ClientQuickView from './ClientQuickView';
import RecentCallsWidget from './RecentCallsWidget';
import UpcomingAppointmentsWidget from '../../call/UpcomingAppointmentsWidget';
import InboxWidget from './InboxWidget';

// Derive backend origin from the same env var the rest of the app uses
// (REACT_APP_API_URL is set on every deployed build; REACT_APP_BACKEND_URL
// is not, which is why this used to fall back to 127.0.0.1 in prod/dev).
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || API_URL.replace(/\/api\/?$/, '');

function DashboardV2({ token }) {
  const navigate = useNavigate();

  // Data state — one per section for independent loading
  const [overview, setOverview] = useState(null);
  const [actionQueue, setActionQueue] = useState(null);
  const [activities, setActivities] = useState(null);

  // Loading state per section
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);

  // Error state per section
  const [errorOverview, setErrorOverview] = useState('');
  const [errorQueue, setErrorQueue] = useState('');
  const [errorActivity, setErrorActivity] = useState('');

  // UrgencyStrip tier filter
  const [filterTier, setFilterTier] = useState(null);

  // Fetch functions
  const fetchOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);
      setErrorOverview('');
      const res = await api.get('/therapist/dashboard/v2/overview');
      setOverview(res.data);
    } catch (err) {
      console.error('Error fetching overview:', err);
      setErrorOverview(err.response?.data?.msg || 'Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchActionQueue = useCallback(async () => {
    try {
      setLoadingQueue(true);
      setErrorQueue('');
      const res = await api.get('/therapist/dashboard/v2/action-queue');
      setActionQueue(res.data);
    } catch (err) {
      console.error('Error fetching action queue:', err);
      setErrorQueue(err.response?.data?.msg || 'Failed to load action queue');
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    try {
      setLoadingActivity(true);
      setErrorActivity('');
      const res = await api.get('/therapist/dashboard/recent-activity?limit=20');
      setActivities(res.data);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setErrorActivity(err.response?.data?.msg || 'Failed to load activity');
    } finally {
      setLoadingActivity(false);
    }
  }, []);

  // Initial load — all three in parallel
  useEffect(() => {
    fetchOverview();
    fetchActionQueue();
    fetchActivities();
  }, [fetchOverview, fetchActionQueue, fetchActivities]);

  // Socket.io for real-time micro-updates
  useEffect(() => {
    const socket = io(BACKEND_URL, { auth: { token } });

    socket.on('connect', () => {
      console.log('Dashboard V2 socket connected');
    });

    socket.on('alert:created', () => {
      fetchOverview();
      fetchActionQueue();
    });

    socket.on('checkin:created', () => {
      fetchOverview();
      fetchActionQueue();
      fetchActivities();
    });

    socket.on('client:activity', () => {
      fetchActivities();
    });

    socket.on('goal:completed', () => {
      fetchOverview();
      fetchActivities();
    });

    socket.on('tass:priorities_updated', () => {
      fetchOverview();
      fetchActionQueue();
    });

    socket.on('supervisor:intervention', () => {
      fetchOverview();
    });

    return () => {
      socket.close();
    };
  }, [token, fetchOverview, fetchActionQueue, fetchActivities]);

  // Navigation helpers
  const handleNavigateToClient = (clientId) => {
    navigate(`/therapist/dashboard?tab=clients&clientId=${clientId}`);
  };

  const handleViewAllClients = () => {
    navigate('/therapist/dashboard?tab=clients');
  };

  // Stale data check (>30 min)
  const isStaleData = overview?.lastPriorityCompute &&
    (Date.now() - new Date(overview.lastPriorityCompute).getTime()) > 30 * 60 * 1000;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, minHeight: 0 }}>

      {/* Stale data banner */}
      {isStaleData && (
        <MuiAlert severity="info" icon={<UpdateDisabled />} sx={{ py: 0 }}>
          <Typography variant="caption">
            Priority data last updated {new Date(overview.lastPriorityCompute).toLocaleTimeString()}
          </Typography>
        </MuiAlert>
      )}

      {/* ROW 1: Urgency Strip */}
      {loadingOverview ? (
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
      ) : errorOverview ? (
        <MuiAlert severity="error" action={<Button size="small" onClick={fetchOverview}>Retry</Button>}>
          {errorOverview}
        </MuiAlert>
      ) : overview && (
        <UrgencyStrip
          tiers={overview.tiers}
          alerts={overview.alerts}
          overdueReviews={overview.overdueReviews}
          caseload={overview.caseload}
          onTierFilter={setFilterTier}
        />
      )}

      {/* ROW 2: Action Queue (collapsed when empty) */}
      {loadingQueue ? (
        <Box sx={{ display: 'flex', gap: 2, px: 2 }}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rectangular" width={240} height={260} sx={{ borderRadius: 2, flexShrink: 0 }} />
          ))}
        </Box>
      ) : errorQueue ? (
        <MuiAlert severity="error" action={<Button size="small" onClick={fetchActionQueue}>Retry</Button>}>
          {errorQueue}
        </MuiAlert>
      ) : (
        <ActionQueue
          items={actionQueue || []}
          filterTier={filterTier}
          onNavigateToClient={handleNavigateToClient}
        />
      )}

      {/* Inbox — non-interrupt notices. Directly under the action queue: still
          below anything genuinely pressing (the urgency strip, the queue, and
          the crisis banner that sits above the whole dashboard), but above the
          stats bar so it is visible without scrolling. It was below the fold on
          first release and simply went unseen. */}
      {/* Reuse the dashboard's own navigation rather than the older state-based
          route copied from CareNoteAlerts: ?tab=clients&clientId=X is what
          every other widget here uses and it survives a reload. */}
      <InboxWidget
        token={token}
        onOpenClient={handleNavigateToClient}
        // The conversation id and message id ride the same navigation state
        // that consultations already use to restore a conversation; tab 4 is
        // Conversations in ClientDetailView.
        onOpenMoment={({ clientId, clientName, conversationId, messageId }) =>
          navigate('/therapist/dashboard?tab=clients', {
            // selectClient must be a client OBJECT, not an id. TherapistDashboard
            // assigns it straight to selectedClient and ClientDetailView reads
            // selectedClient._id, so a bare string opens the right tab on an
            // empty view ("Client ID or token not provided") — the link appears
            // to work and shows nothing, which is the failure this button exists
            // to avoid. The name is carried so the header is not blank while the
            // full record loads.
            state: {
              selectClient: { _id: clientId, name: clientName },
              openTab: 4,
              conversationId,
              messageId,
              timestamp: Date.now()
            }
          })
        }
      />

      {/* ROW 3: Summary Stats Bar — single horizontal row */}
      {loadingOverview ? (
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
      ) : errorOverview ? null : overview && (
        <SummaryStatsBar
          weeklyProgress={overview.weeklyProgress}
          alerts={overview.alerts}
          engagement={overview.engagement}
          caseload={overview.caseload}
          supervisorNotes={overview.supervisorNotes}
          consultationsThisMonth={overview.consultationsThisMonth}
        />
      )}

      {/* ROW 3.4: Upcoming scheduled calls */}
      <UpcomingAppointmentsWidget
        token={token}
        role="therapist"
      />

      {/* ROW 3.5: Recent video calls */}
      <RecentCallsWidget
        token={token}
        onNavigateToClient={handleNavigateToClient}
      />

      {/* ROW 4: Client Quick View */}
      <ClientQuickView
        onNavigateToClient={handleNavigateToClient}
        onViewAllClients={handleViewAllClients}
      />

      {/* ROW 5: Activity Stream — only shows when there's actual data */}
      {loadingActivity ? (
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
      ) : errorActivity ? (
        <MuiAlert severity="error" action={<Button size="small" onClick={fetchActivities}>Retry</Button>}>
          {errorActivity}
        </MuiAlert>
      ) : (
        <ActivityStream
          activities={activities || []}
          onNavigateToClient={handleNavigateToClient}
        />
      )}
    </Box>
  );
}

export default DashboardV2;
